import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotifications } from './NotificationsProvider';

// Remember only the explicit choice made in this open settings section. Returning
// from OS Settings completes that choice after permission AND registration succeed.
export default function useNotificationToggle({ active, draft, setDraft, setFeedback, generation }) {
  const { enablePush, deviceStatus } = useNotifications();
  const [waiting, setWaiting] = useState(null);
  const busy = useRef(false), sequence = useRef(0), current = useRef(null);
  current.current = { active };
  const valid = useCallback(intent => current.current.active && generation.current === intent.generation && sequence.current === intent.sequence, [generation]);
  const invalidate = useCallback(() => { sequence.current++; busy.current = false; }, []);
  useEffect(() => {
    if (!active) { invalidate(); setWaiting(null); }
    return invalidate;
  }, [active, invalidate]);
  useEffect(() => {
    if (waiting && valid(waiting) && deviceStatus?.permission === 'granted' && deviceStatus?.registration === 'registered') {
      setDraft(previous => ({ ...previous, [waiting.key]: true }));
      setFeedback(''); setWaiting(null);
    }
  }, [waiting, deviceStatus?.permission, deviceStatus?.registration, active, setDraft, setFeedback, valid]);

  return async key => {
    if (busy.current || !active) return;
    const intent = { key, generation: generation.current, sequence: ++sequence.current };
    setWaiting(null); setFeedback('');
    const value = !draft[key];
    if (value) {
      busy.current = true;
      try {
        const result = await enablePush();
        if (!valid(intent)) return;
        if (result?.settingsOpened) { setWaiting(intent); return; }
      } catch (error) {
        if (valid(intent) && !['PUSH_PERMISSION_CANCELLED', 'PUSH_SESSION_CHANGED'].includes(error.message)) {
          setFeedback(`notifications:${error.message === 'PUSH_PERMISSION_DENIED' ? 'permissionDenied' : 'registrationFailed'}`);
        }
        return;
      } finally { if (sequence.current === intent.sequence) busy.current = false; }
    }
    if (valid(intent)) setDraft(previous => ({ ...previous, [key]: value }));
  };
}
